import { authService, databaseService } from "../firebase/firebase";
import { USER_ROLES, ADMIN_EMAIL } from "../utils/constants";

/**
 * Resolve a user's role from the database. Falls back to the configured admin
 * email only for the very first user bootstrap scenario.
 */
const resolveUserRole = async (uid, email) => {
  const userRecord = await databaseService.read(`users/${uid}`);
  if (userRecord?.role) {
    return userRecord.role;
  }
  if (email === ADMIN_EMAIL) {
    return USER_ROLES.ADMIN;
  }
  return USER_ROLES.EMPLOYEE;
};

/**
 * Normalize a stored timestamp to a number. Existing string timestamps are
 * converted; missing or invalid values fall back to the current time.
 */
const normalizeTimestamp = (value) => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return Date.now();
};

/**
 * Ensure the /users/{uid} document exists and is up-to-date.
 */
const ensureUserDocument = async (uid, email) => {
  const existing = await databaseService.read(`users/${uid}`);
  const expectedRole = await resolveUserRole(uid, email);

  if (existing && existing.role === expectedRole) {
    return expectedRole;
  }

  await databaseService.set(`users/${uid}`, {
    uid,
    role: expectedRole,
    email: email || existing?.email,
    createdAt: normalizeTimestamp(existing?.createdAt),
  });

  return expectedRole;
};

/**
 * Authentication API Service
 */
export const authServiceApi = {
  /**
   * Login with email and password
   */
  login: async (email, password) => {
    const authData = await authService.loginWithEmail(email, password);

    const role = await ensureUserDocument(authData.uid, authData.email);

    const expiryTime = Date.now() + 3600 * 1000;

    return {
      token: authData.token,
      refreshToken: authData.refreshToken,
      role,
      uid: authData.uid,
      email: authData.email,
      expiryTime,
    };
  },

  /**
   * Logout current user
   */
  logout: async () => {
    await authService.logout();
  },

  /**
   * Refresh ID token
   */
  refreshToken: async (user) => {
    if (!user) throw new Error("No user available for token refresh");
    const token = await authService.getIdToken(user);
    const expiryTime = Date.now() + 3600 * 1000;
    return { token, expiryTime };
  },

  /**
   * Get current user role from /users/{uid}
   */
  getCurrentUserRole: async (uid) => {
    const role = await resolveUserRole(uid);
    return role;
  },

  /**
   * Check if current user is admin
   */
  isAdmin: async (uid) => {
    const role = await resolveUserRole(uid);
    return role === USER_ROLES.ADMIN;
  },

  /**
   * Get employee profile
   */
  getEmployee: async (uid) => {
    return databaseService.read(`employees/${uid}`);
  },
};
